import { Container, Table } from '@material-ui/core';
import { TableHeader } from 'components/table/table-header/table-header';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import style from 'styles/variables-for-js.module.scss';
import { useEventListener } from 'utils/use-event-listener';
import { TableStickyHeaderProps } from './table-sticky-header-props';
import './table-sticky-header.scss';

/**
 * Sticky table header component that sticks on the top of page
 * when static table header is not in the view.
 * It's based on `table header` component that is used in static table header too.
 */
export const TableStickyHeader: React.FC<TableStickyHeaderProps> = React.forwardRef((props, ref) => {
  const [fullWidth, setFullWidth] = useState<any>();
  const [cellWidth, setCellWidth] = useState<string[]>([]);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [isBelowTable, setIsBelowTable] = useState<boolean>(false);
  const [headerSpace, setHeaderSpace] = useState<number>();
  const stickyContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Gets all sizes that are needed to match table's columns position and its width.
   */
  const getSizes = useCallback(() => {
    setFullWidth(props.tableHeadRef.current?.scrollWidth);

    const cellsWidth: string[] = [];
    // gets width of all cells from static header to match position of columns in table
    if (props.tableHeadRef.current?.firstChild.cells) {
      Array.from(props.tableHeadRef.current.firstChild.cells).forEach((cell: any) => {
        cellsWidth.push(cell.offsetWidth);
      });
    }
    setCellWidth(cellsWidth);
  }, [props]);

  /**
   * Gets sticky headers heights to calculate space needed to glue sticky header below them
   */
  const getSpace = () => {
    const sgHeader = document.getElementById('sg-header');
    const searchHeader = document.getElementById('sg-search-bar');
    const catalogueHeader = document.getElementById('sg-catalogue-header');
    const sgHeight = sgHeader?.offsetHeight;
    const searchHeaderHeight = searchHeader?.offsetHeight;
    const catalogueHeaderHeight = catalogueHeader?.offsetHeight;

    const heights = [sgHeight, searchHeaderHeight, catalogueHeaderHeight];
    let finalSpace: any = 0;

    heights.forEach((height) => {
      if (height) {
        finalSpace += height;
      }
    });

    if (finalSpace === 0) {
      finalSpace = sgHeight;
    }

    setHeaderSpace(finalSpace);
  };

  useEventListener('resize', getSizes);
  useEventListener('scroll', getSpace);

  useEffect(() => {
    getSizes();
    getSpace();
  }, [getSizes, props]);

  /**
   * Observes the thead element to show/hide the fixed background
   * based on whether the thead is on screen or not.
   *
   * rootMargin offset is based on header and search bar heights.
   */
  useEffect(() => {
    const tableHead = props.tableHeadRef.current;
    const styledHeadersHeights: any = style.stickyHeadersSpace.split('px')[0];
    const space: number = headerSpace || styledHeadersHeights;
    const stickyContainer = stickyContainerRef.current?.parentElement;

    const observer = new IntersectionObserver(([entry]) => {
      const windowHeight = window.innerHeight;
      const isBelow = entry.boundingClientRect.top > (windowHeight - space);

      // Only set sticky if boundingClientRect.y is not 0 to prevent it
      // being set when initially loading
      // and check if header is not visible because it's lower in page (when tooltip opens)
      const sticky = entry.boundingClientRect.y !== 0 && !entry.isIntersecting && !isBelow;
      if (isSticky) {
        // set sticky header below main header and search bar
        // eslint-disable-next-line no-unused-expressions
        stickyContainer?.setAttribute('style', `transform: translateY(${space}px);`);
      } else {
        // eslint-disable-next-line no-unused-expressions
        stickyContainer?.removeAttribute('style');
      }
      setIsSticky(sticky);
    }, { threshold: 0, rootMargin: `${-1 * space}px` });
    observer.observe(tableHead as HTMLElement);

    return () => {
      observer.unobserve(tableHead as HTMLElement);
    };
  }, [headerSpace, isSticky, props.tableHeadRef]);

  /**
   * Hides sticky header if scrolled to the end of table
   */
  useEffect(() => {
    const table = props.tableRef.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        setIsBelowTable(entry.boundingClientRect.y < 0);
      } else {
        setIsBelowTable(false);
      }
    }, { threshold: 0 });
    observer.observe(table as HTMLElement);

    return () => {
      observer.unobserve(table as HTMLElement);
    };
  }, [props.tableRef]);

  return (
    <div className={`sticky-header  ${isSticky && !isBelowTable ? '' : 'sticky-header--hidden'}`}>
      <Container className="sg-table sg-table__wrapper table-scroll-shadow" ref={stickyContainerRef}>
        <Table className="sg-table__table" style={{ minWidth: fullWidth }} ref={ref}>
          <TableHeader
            showInfoIcon={false}
            columns={props.columns}
            headerSelected={props.onHeaderSelected}
            enableSort={props.enableSort}
            cellsWidth={cellWidth}
            sortBy={props.sortBy}
          />
        </Table>
      </Container>
    </div>
  );
});

// (TableStickyHeader as any).whyDidYouRender = true;
